import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import { DollarSign, TrendingUp, Download, Gift, FileText, CheckSquare } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Commissions({ user, onLogout }) {
  const [commissions, setCommissions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [cooperatives, setCooperatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [selectedInstallation, setSelectedInstallation] = useState("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("");
  const [selectedCommissionStatus, setSelectedCommissionStatus] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Estado para seleção múltipla
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null); // 'commission' or 'bonus'
  const [bulkActionStatus, setBulkActionStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDashboardData();
  }, [selectedPartner, selectedInstallation, selectedPaymentStatus, selectedCommissionStatus]);

  const fetchData = async () => {
    try {
      const params = {};
      if (selectedPartner) params.partner_name = selectedPartner;
      if (selectedPaymentStatus) params.payment_status = selectedPaymentStatus;
      if (selectedCommissionStatus) params.commission_status = selectedCommissionStatus;
      
      const [commRes, partnerRes, coopRes] = await Promise.all([
        axiosInstance.get("/commissions", { params }),
        axiosInstance.get("/partners"),
        axiosInstance.get("/cooperatives")
      ]);
      setCommissions(commRes.data);
      setPartners(partnerRes.data);
      setCooperatives(coopRes.data);
      // Limpar seleção quando dados mudam
      setSelectedItems(new Set());
    } catch (error) {
      toast.error("Erro ao carregar comissões");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async (partnerId) => {
    if (!partnerId) {
      setSummary(null);
      return;
    }
    try {
      const response = await axiosInstance.get(`/commissions/summary/${partnerId}`);
      setSummary(response.data);
    } catch (error) {
      toast.error("Erro ao carregar resumo");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const params = {};
      if (selectedPartner) params.partner_name = selectedPartner;
      if (selectedPaymentStatus) params.payment_status = selectedPaymentStatus;
      if (selectedCommissionStatus) params.commission_status = selectedCommissionStatus;
      
      const response = await axiosInstance.get("/commissions/dashboard", { params });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Erro ao carregar dashboard");
    }
  };

  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      const response = await axiosInstance.get("/commissions/export/csv", {
        responseType: "blob",
        params: selectedPartner ? { partner_name: selectedPartner } : {}
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "comissoes.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Relatório exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar relatório");
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const params = {};
      if (selectedPartner) params.partner_name = selectedPartner;
      if (selectedPaymentStatus) params.payment_status = selectedPaymentStatus;
      if (selectedCommissionStatus) params.commission_status = selectedCommissionStatus;
      
      const response = await axiosInstance.get("/commissions/report/pdf", {
        responseType: "blob",
        params
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_comissoes_${selectedPartner || 'todos'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Relatório PDF gerado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar relatório PDF");
    }
  };

  // Funções para seleção múltipla
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const openBulkActionDialog = (actionType) => {
    setBulkActionType(actionType);
    setBulkActionStatus('');
    setBulkActionDialogOpen(true);
  };

  const handleBulkUpdate = async () => {
    if (!bulkActionStatus || selectedItems.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const invoiceIds = Array.from(selectedItems);
      const response = await axiosInstance.put("/invoices/bulk-update", {
        invoice_ids: invoiceIds,
        action_type: bulkActionType,
        status: bulkActionStatus
      });
      
      toast.success(response.data.message);
      setBulkActionDialogOpen(false);
      setSelectedItems(new Set());
      fetchData();
    } catch (error) {
      console.error("Error updating:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setBulkUpdating(false);
    }
  };

  const canEdit = () => {
    return user?.role === 'admin' || user?.role === 'operador';
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-green-100', text: 'text-green-700', label: 'Pago' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pendente' },
      'overdue': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Vencido' },
      'defaulted': { bg: 'bg-red-100', text: 'text-red-700', label: 'Negativado' }
    };
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
        status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
      }`}>
        {status === 'paid' ? 'Pago' : 'Pendente'}
      </span>
    );
  };

  // Filtering now handled by backend
  const filteredCommissions = selectedInstallation
    ? commissions.filter(c => (c.installation_number || '').trim() === selectedInstallation)
    : commissions;

  // Calcular itens da página atual
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCommissions = filteredCommissions.slice(startIndex, endIndex);

  // Função de seleção de todos da página
  const handleSelectAllPage = () => {
    const pageIds = paginatedCommissions.map(c => c.invoice_id || c.id);
    const allPageSelected = pageIds.every(id => selectedItems.has(id));
    
    if (allPageSelected) {
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        pageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        pageIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Comissões e Bônus</h1>
            <p className="text-gray-600">Acompanhe as comissões e bônus gerados</p>
          </div>
          <div className="flex gap-2">
            {user?.role !== 'operador' && (
              <Button onClick={handleGeneratePDF} className="bg-blue-600 hover:bg-blue-700">
                <FileText className="w-4 h-4 mr-2" />
                Gerar PDF
              </Button>
            )}
            <Button onClick={handleExportCSV} className="bg-emerald-600 hover:bg-emerald-700">
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {dashboardData && (
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Comissões Pendentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  R$ {dashboardData.total_pending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Comissões Pagas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  R$ {dashboardData.total_paid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Total de Bônus
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  R$ {dashboardData.total_bonus.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Filtros</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parceiro</label>
                <select
                  data-testid="partner-filter-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={selectedPartner}
                  onChange={(e) => {
                    setSelectedPartner(e.target.value);
                    setSelectedInstallation(""); // reset installation filter ao trocar de parceiro
                    fetchSummary(e.target.value);
                  }}
                >
                  <option value="">Todos os parceiros</option>
                  {partners
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                    .map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                </select>
              </div>

              {/* Filtro por Número de Instalação — aparece após selecionar Parceiro */}
              {selectedPartner && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número de Instalação</label>
                  <select
                    data-testid="installation-filter-select"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={selectedInstallation}
                    onChange={(e) => setSelectedInstallation(e.target.value)}
                  >
                    <option value="">Todos as instalações</option>
                    {Array.from(new Set(
                      commissions
                        .map(c => (c.installation_number || '').trim())
                        .filter(Boolean)
                    ))
                      .sort()
                      .map(inst => (
                        <option key={inst} value={inst}>{inst}</option>
                      ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status de Pagamento da Fatura</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={selectedPaymentStatus}
                  onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                  <option value="overdue">Vencido</option>
                  <option value="defaulted">Inadimplente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status da Comissão</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={selectedCommissionStatus}
                  onChange={(e) => setSelectedCommissionStatus(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {summary && (
            <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <TrendingUp className="w-5 h-5" />
                  Resumo do Parceiro
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total de Comissões:</span>
                  <span className="font-bold text-gray-800" data-testid="summary-count">{summary.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Valor Total:</span>
                  <span className="font-bold text-emerald-600" data-testid="summary-total">
                    R$ {summary.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pendente:</span>
                  <span className="font-bold text-yellow-600" data-testid="summary-pending">
                    R$ {summary.pending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pago:</span>
                  <span className="font-bold text-green-600" data-testid="summary-paid">
                    R$ {summary.paid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Histórico de Comissões
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Barra de Ações em Lote */}
            {canEdit() && selectedItems.size > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {selectedItems.size} item(s) selecionado(s)
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => openBulkActionDialog('commission')}
                    className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                    size="sm"
                  >
                    Atualizar Comissão
                  </Button>
                  <Button
                    onClick={() => openBulkActionDialog('bonus')}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                    size="sm"
                  >
                    Atualizar Bônus
                  </Button>
                  <Button
                    onClick={() => setSelectedItems(new Set())}
                    variant="outline"
                    size="sm"
                  >
                    Limpar Seleção
                  </Button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : filteredCommissions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma comissão encontrada.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {canEdit() && (
                        <TableHead className="w-10">
                          <Checkbox
                            checked={paginatedCommissions.length > 0 && paginatedCommissions.every(c => selectedItems.has(c.invoice_id || c.id))}
                            onCheckedChange={handleSelectAllPage}
                            aria-label="Selecionar todas da página"
                          />
                        </TableHead>
                      )}
                      <TableHead>Parceiro</TableHead>
                      <TableHead>Cooperado</TableHead>
                      <TableHead>Nº Instalação</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                      <TableHead>Data Vencimento</TableHead>
                      <TableHead>Valor Fatura</TableHead>
                      <TableHead>Percentual</TableHead>
                      <TableHead>Valor Comissão</TableHead>
                      <TableHead>Bônus</TableHead>
                      <TableHead>Status Fatura</TableHead>
                      <TableHead>Status Comissão</TableHead>
                      <TableHead>Status Bônus</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedCommissions.map((commission) => (
                      <TableRow 
                        key={commission.id} 
                        data-testid={`commission-row-${commission.id}`}
                        className={selectedItems.has(commission.invoice_id || commission.id) ? "bg-blue-50" : ""}
                      >
                        {canEdit() && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.has(commission.invoice_id || commission.id)}
                              onCheckedChange={() => handleSelectItem(commission.invoice_id || commission.id)}
                              aria-label={`Selecionar ${commission.cooperative_name}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">{commission.partner_name || '-'}</TableCell>
                        <TableCell>{commission.cooperative_name || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{commission.installation_number || '-'}</TableCell>
                        <TableCell className="text-sm">{commission.reference_month || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {commission.signature_due_date 
                            ? new Date(commission.signature_due_date).toLocaleDateString('pt-BR')
                            : '-'}
                        </TableCell>
                        <TableCell className="font-semibold">
                          R$ {(commission.invoice_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-sm">{commission.commission_percentage?.toFixed(2)}%</TableCell>
                        <TableCell className="font-semibold text-emerald-600">
                          R$ {(commission.commission_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="font-semibold text-purple-600">
                          R$ {(commission.bonus || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          {getPaymentStatusBadge(commission.payment_status)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(commission.commission_status)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(commission.bonus_status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                
                {/* Pagination */}
                {(() => {
                  const totalPages = Math.ceil(filteredCommissions.length / itemsPerPage);
                  return (
                    <div className="flex items-center justify-between mt-4">
                      {/* Items per page selector */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Itens por página:</span>
                        <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-sm text-gray-500">
                          ({filteredCommissions.length} registros)
                        </span>
                      </div>
                      
                      {/* Pagination buttons */}
                      {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            Anterior
                          </Button>
                          <span className="text-sm text-gray-600">
                            Página {currentPage} de {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Próxima
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Ação em Lote */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkActionType === 'commission' && 'Atualizar Status de Comissão'}
              {bulkActionType === 'bonus' && 'Atualizar Status de Bônus'}
            </DialogTitle>
            <DialogDescription>
              Esta ação será aplicada a <strong>{selectedItems.size}</strong> item(s) selecionado(s).
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="bulk-status" className="mb-2 block">
              Selecione o novo status:
            </Label>
            <Select value={bulkActionStatus} onValueChange={setBulkActionStatus}>
              <SelectTrigger id="bulk-status">
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setBulkActionDialogOpen(false)}
              disabled={bulkUpdating}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleBulkUpdate}
              disabled={!bulkActionStatus || bulkUpdating}
              className={
                bulkActionType === 'commission' 
                  ? 'bg-gradient-to-r from-purple-500 to-violet-600' 
                  : 'bg-gradient-to-r from-amber-500 to-orange-600'
              }
            >
              {bulkUpdating ? 'Atualizando...' : `Atualizar ${selectedItems.size} item(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
